import { motion } from "framer-motion";
import { Check, Cog, Globe, Rocket } from "lucide-react";
import type { Business, BoldMove, MoveKind } from "@/lib/types";
import { cn } from "@/lib/cn";

const ICONS: Record<MoveKind, React.ComponentType<{ className?: string }>> = {
  automate: Cog,
  launch: Rocket,
  expand: Globe,
};

const LABELS: Record<MoveKind, string> = {
  automate: "Automate",
  launch: "Launch",
  expand: "Expand",
};

const COLORS: Record<MoveKind, string> = {
  automate: "#5EEAFF",
  launch: "#FFC36A",
  expand: "#A4F36F",
};

type Props = {
  business: Business;
  className?: string;
  highlightCommitment?: boolean;
  onCommit?: (kind: MoveKind) => void;
};

export function BusinessCard({ business, className, highlightCommitment, onCommit }: Props) {
  const accent = business.accent || "#5EEAFF";

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={cn("flex flex-col gap-5", className)}
    >
      <div className="relative overflow-hidden rounded-xl p-6 glass glow-border">
        <div
          aria-hidden
          className="absolute -top-24 -right-24 h-64 w-64 rounded-full opacity-30 blur-3xl"
          style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)` }}
        />
        <div className="relative flex items-start gap-4">
          <div
            className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg text-3xl"
            style={{ background: `linear-gradient(135deg, ${accent}33, ${accent}10)` }}
          >
            {business.emoji || "✨"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-widest text-white/50">
              {business.creatorName ? `${business.creatorName}'s reimagined business` : "Your reimagined business"}
            </div>
            <h3 className="mt-1 text-xl font-semibold leading-tight text-white">
              {business.headline}
            </h3>
            {business.industry && (
              <p className="mt-1 text-xs text-white/60">{business.industry}</p>
            )}
          </div>
        </div>
        <p className="relative mt-5 text-sm leading-relaxed text-white/85">
          {business.summary}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {business.moves.map((m, i) => (
          <MoveCard
            key={m.kind + i}
            move={m}
            highlight={highlightCommitment && business.commitment === m.kind}
            onCommit={onCommit ? () => onCommit(m.kind) : undefined}
          />
        ))}
      </div>
    </motion.div>
  );
}

function MoveCard({
  move,
  highlight,
  onCommit,
}: {
  move: BoldMove;
  highlight?: boolean;
  onCommit?: () => void;
}) {
  const Icon = ICONS[move.kind];
  const color = COLORS[move.kind];
  const label = LABELS[move.kind];

  return (
    <motion.div
      whileHover={{ y: -2 }}
      className={cn(
        "relative overflow-hidden rounded-xl p-5 glass transition",
        highlight && "ring-2 ring-cyan-glow",
      )}
      style={{ borderColor: `${color}33` }}
    >
      <div
        aria-hidden
        className="absolute -top-12 -right-12 h-40 w-40 rounded-full opacity-25 blur-2xl"
        style={{ background: `radial-gradient(circle, ${color}, transparent 70%)` }}
      />
      <div className="relative">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{ background: `${color}22`, color }}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div
              className="text-[10px] font-semibold uppercase tracking-widest"
              style={{ color }}
            >
              {label}
            </div>
            <div className="text-base font-semibold leading-tight text-white">
              {move.title}
            </div>
          </div>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-white/85">{move.why}</p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="First step this week" tone={color}>
            {move.first_step}
          </Field>
          <Field label="In 90 days" tone={color}>
            {move.ninety_day}
          </Field>
        </div>

        <div className="mt-3">
          <Field label="Risk to watch" tone="#FF9F6A" subtle>
            {move.risk}
          </Field>
        </div>

        {onCommit && (
          <button
            onClick={onCommit}
            className={cn(
              "mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition",
              highlight
                ? "bg-cyan-glow text-ink-900"
                : "border border-white/10 bg-white/5 text-white hover:bg-white/10",
            )}
          >
            {highlight ? <Check className="h-4 w-4" /> : null}
            {highlight ? "This is your move" : `I'll act on ${label} this month`}
          </button>
        )}
      </div>
    </motion.div>
  );
}

function Field({
  label,
  tone,
  subtle,
  children,
}: {
  label: string;
  tone: string;
  subtle?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-lg p-3"
      style={{
        background: subtle ? `${tone}08` : `${tone}14`,
        border: `1px solid ${tone}22`,
      }}
    >
      <div
        className="mb-1 text-[10px] font-semibold uppercase tracking-widest"
        style={{ color: tone }}
      >
        {label}
      </div>
      <p className="text-sm leading-relaxed text-white/85">{children}</p>
    </div>
  );
}
